import React, { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Button,
    IconButton,
    useTheme,
    useMediaQuery,
    Box,
    Typography,
    Slide
} from "@material-ui/core";
import {
    Close as CloseIcon
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import PlanManagerForm from "../PlanManagerForm";
import ConfirmationModal from "../ConfirmationModal";
import toastUtils from "../../utils/toastUtils";

// Helper function to ensure i18n returns a string
const t = (key, fallback = '') => {
    const result = i18n.t(key);
    return typeof result === 'string' ? result : (fallback || key.split('.').pop());
};

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            borderRadius: theme.spacing(2),
            [theme.breakpoints.down('sm')]: {
                margin: 0,
                width: '100%',
                height: '100%',
                maxHeight: '100%',
                maxWidth: '100%',
                borderRadius: 0,
            }
        }
    },
    dialogTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2),
            position: 'sticky',
            top: 0,
            zIndex: 1,
        }
    },
    titleText: {
        fontSize: '1.5rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.25rem',
        }
    },
    closeButton: {
        color: theme.palette.text.secondary,
        padding: theme.spacing(1),
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        }
    },
    dialogContent: {
        padding: 0,
        backgroundColor: theme.palette.background.default,
        [theme.breakpoints.down('sm')]: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
        }
    },
    dialogActions: {
        padding: theme.spacing(2, 3),
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        gap: theme.spacing(1),
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2),
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
        }
    },
    actionButton: {
        minWidth: 120,
        borderRadius: theme.spacing(3),
        fontWeight: 600,
        textTransform: 'none',
        [theme.breakpoints.down('sm')]: {
            minWidth: 100,
            flex: 1,
        }
    },
    primaryButton: {
        background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
        color: 'white',
        '&:hover': {
            background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
        },
        '&:disabled': {
            background: theme.palette.action.disabledBackground,
            color: theme.palette.action.disabled,
        }
    },
    secondaryButton: {
        borderColor: theme.palette.divider,
        color: theme.palette.text.secondary,
        '&:hover': {
            borderColor: theme.palette.text.secondary,
            backgroundColor: theme.palette.action.hover,
        }
    },
    deleteButton: {
        backgroundColor: theme.palette.error.main,
        color: 'white',
        '&:hover': {
            backgroundColor: theme.palette.error.dark,
        }
    }
}));

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const PlanModal = ({
    open,
    mode = 'create', // 'create' | 'edit'
    initialValues = null,
    onClose,
    onSave,
    onDelete,
    loading = false
}) => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [isClosing, setIsClosing] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [formData, setFormData] = useState(null);
    const formRef = useRef(null);

    // Reset form data when modal opens/closes or mode changes
    useEffect(() => {
        if (open) {
            if (mode === 'edit' && initialValues) {
                setFormData(initialValues);
            } else if (mode === 'create') {
                setFormData(null); // Let form use its default values
            }
        } else {
            // Reset form data when modal closes
            setFormData(null);
            setShowDeleteConfirm(false);
        }
    }, [open, mode, initialValues]);

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (!open) return;

            switch (event.key) {
                case 'Escape':
                    if (!loading && !showDeleteConfirm) {
                        handleClose();
                    }
                    break;
                case 'Enter':
                    if (event.ctrlKey || event.metaKey) {
                        event.preventDefault();
                        if (!loading) {
                            // Trigger form submission
                            handleFormSubmit();
                        }
                    }
                    break;
                default:
                    break;
            }
        };

        if (open) {
            document.addEventListener('keydown', handleKeyDown);
            // Prevent background scrolling on mobile
            if (isMobile) {
                document.body.style.overflow = 'hidden';
            }
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            if (isMobile) {
                document.body.style.overflow = 'unset';
            }
        };
    }, [open, loading, showDeleteConfirm, isMobile]);

    const handleClose = () => {
        if (loading) return;
        
        setIsClosing(true);
        setTimeout(() => {
            setIsClosing(false);
            setFormData(null); // Reset form data
            onClose();
        }, 150);
    };

    const handleFormSubmit = () => {
        // Trigger form submission programmatically
        const form = document.getElementById('plan-form');
        if (form) {
            form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
    };

    const handleFormSubmitCallback = async (data) => {
        try {
            await onSave(data);
            // Success - modal will be closed by parent component
            toastUtils.success(
                mode === 'create' ? 'Plano criado com sucesso!' : 'Plano atualizado com sucesso!',
                'Sucesso'
            );
        } catch (error) {
            // Error handling is done in the form component
            console.error('Modal form submission error:', error);
            throw error; // Re-throw to let form handle it
        }
    };

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await onDelete(initialValues);
            setShowDeleteConfirm(false);
            toastUtils.success('Plano excluído com sucesso!', 'Sucesso');
            // Success - modal will be closed by parent component
        } catch (error) {
            console.error('Delete error:', error);
            toastUtils.error(
                error.response?.data?.message || 'Erro ao excluir o plano. Tente novamente.',
                'Erro'
            );
        }
    };

    const handleFormCancel = () => {
        handleClose();
    };

    const getModalTitle = () => {
        switch (mode) {
            case 'edit':
                return t("plans.modal.editTitle", "Editar Plano");
            case 'create':
            default:
                return t("plans.modal.createTitle", "Criar Novo Plano");
        }
    };

    const getModalSubtitle = () => {
        switch (mode) {
            case 'edit':
                return t("plans.modal.editSubtitle", "Modifique os recursos e configurações do plano");
            case 'create':
            default:
                return t("plans.modal.createSubtitle", "Configure os recursos e limites do novo plano");
        }
    };

    return (
        <Dialog
            open={open && !isClosing}
            onClose={handleClose}
            className={classes.dialog}
            maxWidth="lg"
            fullWidth
            fullScreen={isMobile}
            TransitionComponent={isMobile ? Transition : undefined}
            disableBackdropClick={loading}
            disableEscapeKeyDown={loading}
            aria-labelledby="plan-modal-title"
            aria-describedby="plan-modal-description"
        >
            <DialogTitle className={classes.dialogTitle} disableTypography>
                <Box>
                    <Typography 
                        id="plan-modal-title"
                        className={classes.titleText}
                        component="h2"
                    >
                        {getModalTitle()}
                    </Typography>
                    <Typography 
                        id="plan-modal-description"
                        variant="body2" 
                        color="textSecondary"
                        style={{ marginTop: 4 }}
                    >
                        {getModalSubtitle()}
                    </Typography>
                </Box>
                <IconButton
                    className={classes.closeButton}
                    onClick={handleClose}
                    disabled={loading}
                    aria-label={t("plans.modal.close", "Fechar")}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent className={classes.dialogContent} dividers>
                <div ref={formRef}>
                    <PlanManagerForm
                        mode={mode}
                        initialValue={formData}
                        onSubmit={handleFormSubmitCallback}
                        onDelete={handleDeleteClick}
                        onCancel={handleFormCancel}
                        loading={loading}
                        hideActions={true}
                    />
                </div>
            </DialogContent>

            <DialogActions className={classes.dialogActions}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    variant="outlined"
                    className={`${classes.actionButton} ${classes.secondaryButton}`}
                >
                    {t("plans.modal.cancel", "Cancelar")}
                </Button>
                
                {mode === 'edit' && onDelete && initialValues && (
                    <Button
                        onClick={handleDeleteClick}
                        disabled={loading}
                        variant="contained"
                        className={`${classes.actionButton} ${classes.deleteButton}`}
                    >
                        {t("plans.modal.delete", "Excluir")}
                    </Button>
                )}
                
                <Button
                    onClick={handleFormSubmit}
                    disabled={loading}
                    variant="contained"
                    className={`${classes.actionButton} ${classes.primaryButton}`}
                >
                    {loading 
                        ? t("plans.modal.saving", "Salvando...") 
                        : t("plans.modal.save", "Salvar")
                    }
                </Button>
            </DialogActions>

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                title="Confirmar Exclusão"
                open={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={handleDeleteConfirm}
            >
                {t("plans.modal.confirmDelete", "Tem certeza que deseja excluir este plano? Esta ação não pode ser desfeita.")}
            </ConfirmationModal>
        </Dialog>
    );
};

export default PlanModal;